import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import message from './utils/messages';
import dates from './utils/dates';
import localizer from './localizer';

import DayColumn from './DayColumn';
import EventRow from './EventRow';
import TimeColumn from './TimeColumn';
import BackgroundCells from './BackgroundCells';

import { dateFormat } from './utils/propTypes';

import { notify } from './utils/helpers';
import { navigate } from './utils/constants';
import { accessor as get } from './utils/accessors';

import {
  inRange,
  eventSegments,
  endOfRange,
  eventLevels,
  sortEvents,
  segStyle
} from './utils/eventLevels';

const MIN_ROWS = 2;

const TimeGrid = (props) => {
  const {
    events = [],
    start,
    end,
    startAccessor,
    endAccessor,
    allDayAccessor,
    width: propWidth,
    min,
    max,
    scrollToTime,
    dayFormat,
    culture,
    messages: propMessages,
    rtl,
    components,
    titleAccessor,
    eventPropGetter,
    selectable,
    onNavigate,
    onSelectEvent,
    ...otherProps
  } = props;


  const [gutterWidth, setGutterWidth] = useState(undefined);
  const [isOverflowing, setIsOverflowing] = useState(null);
  const [scrollRatio, setScrollRatio] = useState(null);
  const [updatingOverflow, setUpdatingOverflow] = useState(false);

  const contentRef = useRef(null);
  const headerCellRef = useRef(null);
  const allDayRef = useRef(null);
  const guttersRef = useRef([]);
  const slotsRef = useRef(0);

  const messages = message(propMessages);

  // Helper functions to replace dom-helpers
  const getWidth = (element) => {
    return element ? element.offsetWidth : 0;
  };

  const scrollbarSize = () => {
    const div = document.createElement('div');
    div.style.width = '100px';
    div.style.height = '100px';
    div.style.overflow = 'scroll';
    div.style.position = 'absolute';
    div.style.top = '-9999px';
    document.body.appendChild(div);
    const scrollbarWidth = div.offsetWidth - div.clientWidth;
    document.body.removeChild(div);
    return scrollbarWidth;
  };

  const calculateScroll = useCallback(() => {
    if (!scrollToTime || !max || !min) {
      console.warn('calculateScroll: Missing required date parameters', {
        scrollToTime,
        max,
        min
      });
      return;
    }

    // Check for invalid dates
    if (isNaN(scrollToTime.getTime()) || isNaN(max.getTime()) || isNaN(min.getTime())) {
      console.warn('calculateScroll: Invalid date parameters');
      return;
    }

    try {
      const startOfDay = dates.startOf(scrollToTime, 'day');
      if (!startOfDay) {
        console.warn('calculateScroll: Could not get start of day');
        return;
      }

      const diffMillis = scrollToTime - startOfDay;
      const totalMillis = dates.diff(max, min);
      setScrollRatio(diffMillis / totalMillis);
    } catch (error) {
      console.error('Error in calculateScroll:', error);
    }
  }, [scrollToTime, min, max]);

  const applyScroll = useCallback(() => {
    if (scrollRatio && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight * scrollRatio;
      setScrollRatio(null);
    }
  }, [scrollRatio]);

  const checkOverflow = useCallback(() => {
    if (updatingOverflow || !contentRef.current) return;

    const overflowing = contentRef.current.scrollHeight > contentRef.current.clientHeight;

    if (isOverflowing !== overflowing) {
      setUpdatingOverflow(true);
      setIsOverflowing(overflowing);
      setUpdatingOverflow(false);
    }
  }, [isOverflowing, updatingOverflow]);

  const measureGutter = useCallback(() => {
    if (propWidth == null && !gutterWidth) {
      const widths = guttersRef.current.map(getWidth);
      const maxWidth = Math.max(...widths.filter(width => width > 0));

      if (maxWidth > 0) {
        setGutterWidth(maxWidth);
      }
    }
  }, [propWidth, gutterWidth]);

  const headerClick = useCallback((date) => (e) => {
    e.preventDefault();
    notify(onNavigate, [navigate.DATE, date]);
  }, [onNavigate]);

  const selectEvent = useCallback((...args) => {
    notify(onSelectEvent, args);
  }, [onSelectEvent]);

  useEffect(() => {
    calculateScroll();
  }, [calculateScroll]);

  useEffect(() => {
    checkOverflow();
    measureGutter();
    applyScroll();
  }, [checkOverflow, measureGutter, applyScroll]);

  useEffect(() => {
    const { start: currentStart, scrollToTime: currentScrollToTime } = props;
    if (!dates.eq(props.start, currentStart) || props.scrollToTime !== currentScrollToTime) {
      calculateScroll();
    }
  }, [props.start, props.scrollToTime, calculateScroll]);

  const renderHeaderCells = useCallback((range) => {
    return range.map((date, i) => (
      <div key={i} className="rbc-header">
        <a href="#" onClick={headerClick(date)}>
          {localizer.format(date, dayFormat, culture)}
        </a>
      </div>
    ));
  }, [dayFormat, culture, headerClick]);

  const renderAllDayEvents = useCallback((range, levels) => {
    const rangeEnd = endOfRange(range);
    if (!rangeEnd) return null;

    const { first, last } = rangeEnd;
    const rows = levels ? [...levels] : [];

    while (rows.length < MIN_ROWS) {
      rows.push([]);
    }

    return rows.map((segs, idx) => (
      <EventRow
        eventComponent={components.event}
        titleAccessor={titleAccessor}
        startAccessor={startAccessor}
        endAccessor={endAccessor}
        allDayAccessor={allDayAccessor}
        eventPropGetter={eventPropGetter}
        onSelect={selectEvent}
        slots={slotsRef.current}
        key={idx}
        segments={segs}
        start={first}
        end={last}
      />
    ));
  }, [components, titleAccessor, startAccessor, endAccessor, allDayAccessor, eventPropGetter, selectEvent]);

  const renderEvents = useCallback((range, events, today) => {
    if (!range || !events) {
      return null;
    }
    
    return range.map((date, idx) => {
      if (!date) return null;
      
      const daysEvents = events.filter(event => {
        const eventStart = get(event, startAccessor);
        const eventEnd = get(event, endAccessor);
        
        if (!eventStart || !eventEnd) return false;
        
        return dates.inRange(
          date,
          eventStart,
          eventEnd,
          'day'
        );
      });
  
  
      return (
        <DayColumn
          {...otherProps}
          min={dates.merge(date, min)}
          max={dates.merge(date, max)}
          eventComponent={components.event}
          className={cn({ 'rbc-now': dates.eq(date, today, 'day') })}
          style={segStyle(1, slotsRef.current)}
          key={idx}
          date={date}
          events={daysEvents}
          startAccessor={startAccessor}
          endAccessor={endAccessor}
          titleAccessor={titleAccessor}
        />
      );
    });
  }, [min, max, components, startAccessor, endAccessor, otherProps, titleAccessor]);
  const renderHeader = useCallback((range, segments, width) => {
    if (!range || !segments) return null;

    const levelsResult = eventLevels(segments);
    const { levels } = levelsResult || { levels: [] };
    const style = {};

    if (isOverflowing) {
      style[rtl ? 'marginLeft' : 'marginRight'] = `${scrollbarSize()}px`;
    }

    return (
      <div
        ref={headerCellRef}
        className={cn(
          'rbc-time-header',
          isOverflowing && 'rbc-overflowing'
        )}
        style={style}
      >
        <div className="rbc-row">
          <div
            className="rbc-label rbc-header-gutter"
            style={{ width }}
          />
          {renderHeaderCells(range)}
        </div>
        <div className="rbc-row">
          <div
            ref={(ref) => { guttersRef.current[0] = ref; }}
            className="rbc-label rbc-header-gutter"
            style={{ width }}
          >
            {messages.allDay}
          </div>
          <div ref={allDayRef} className="rbc-allday-cell" onClick={selectEvent}>
            <BackgroundCells
              slots={range.length}
              container={() => allDayRef.current}
              selectable={selectable}
              onSelect={selectEvent}
            />
            <div style={{ zIndex: 1, position: 'relative' }}>
              {renderAllDayEvents(range, levels)}
            </div>
          </div>
        </div>
      </div>
    );
  }, [isOverflowing, rtl, messages, selectable, renderHeaderCells, renderAllDayEvents,selectEvent]);

  const gutterRef = useCallback((ref) => {
    guttersRef.current[1] = ref;
  }, []);

  // Main render logic - Add null checks
  if (!start || !end) {
    return <div>Loading calendar...</div>;
  }

  const range = dates.range(start, end, 'day');
  if (!range || range.length === 0) {
    return <div>Invalid date range</div>;
  }

  slotsRef.current = range.length;
  const width = propWidth || gutterWidth;

  const allDayEvents = [];
  const rangeEvents = [];

  events.forEach((event) => {
    const eventStart = get(event, startAccessor);
    const eventEnd = get(event, endAccessor);


    if (!eventStart || !eventEnd) {
      return;
    }

    const isAllDay = get(event, allDayAccessor) ||
      (dates.isJustDate(eventStart) && dates.isJustDate(eventEnd));

    // Use a more reliable range check
    const isInRange = dates.inRange(eventStart, start, end, 'day') ||
      dates.inRange(eventEnd, start, end, 'day') ||
      (eventStart <= start && eventEnd >= end);


    if (isInRange) {
      if (isAllDay) {
        allDayEvents.push(event);
      } else {
        rangeEvents.push(event);
      }
    }
  });

  allDayEvents.sort((a, b) => sortEvents(a, b, props));

  const rangeEnd = endOfRange(range);
  const segments = allDayEvents.map(evt => {
    if (!rangeEnd) return null;
    return eventSegments(evt, rangeEnd.first, rangeEnd.last, props);
  }).filter(seg => seg != null);


  return (
    <div className="rbc-time-view">
      {renderHeader(range, segments, width)}
      <div ref={contentRef} className="rbc-time-content">
        <TimeColumn
          {...props}
          showLabels
          style={{ width }}
          ref={gutterRef}
          className="rbc-time-gutter"
        />
        {renderEvents(range, rangeEvents, props.now)}
      </div>
    </div>
  );
};

TimeGrid.propTypes = {
  ...DayColumn.propTypes,
  ...TimeColumn.propTypes,

  step: PropTypes.number,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  scrollToTime: PropTypes.instanceOf(Date),
  dayFormat: dateFormat,
  rtl: PropTypes.bool,
  events: PropTypes.array,
  start: PropTypes.instanceOf(Date),
  end: PropTypes.instanceOf(Date),
  startAccessor: PropTypes.func,
  endAccessor: PropTypes.func,
  allDayAccessor: PropTypes.func,
  width: PropTypes.number,
  culture: PropTypes.string,
  messages: PropTypes.object,
  components: PropTypes.object,
  titleAccessor: PropTypes.func,
  eventPropGetter: PropTypes.func,
  selectable: PropTypes.bool,
  onNavigate: PropTypes.func,
  onSelectEvent: PropTypes.func,
};

TimeGrid.defaultProps = {
  ...DayColumn.defaultProps,
  ...TimeColumn.defaultProps,

  step: 30,
  min: dates.startOf(new Date(), 'day'),
  max: dates.endOf(new Date(), 'day'),
  scrollToTime: dates.startOf(new Date(), 'day'),
  type: 'gutter',
  now: new Date(),
};

export default TimeGrid;